// Section and block IDs (snippets/anchor.liquid: id="" + data-anchor="").
// - A button set to "Scroll to an ID" (data-scroll-to) and any link to an ID on this page (#reviews, /pages/about#reviews)
//   glide there and stop below the sticky header (scroll-margin-top in critical.css follows --header-offset).
// - Arriving from another page with #id in the address does the same once the page has loaded.
// - Theme editor only: "Show the section ID" / "Show the block ID" puts a chip with the ID on the preview (click to
//   copy); the chip turns red while that ID appears twice on the page, until one is renamed. With it off there is never
//   a chip (every section's ID starts as its type, so repeated sections such as dividers share one until renamed).
(() => {
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const visible = (el) => { const box = el.getBoundingClientRect(); return box.width > 0 || box.height > 0; };

  // The visible element with this ID (a block drawn twice for desktop and mobile resolves to the one on screen).
  const find = (name) => {
    if (!name) return null;
    const all = [...document.querySelectorAll('[data-anchor], [id]')].filter((el) => el.dataset.anchor === name || el.id === name);
    return all.find(visible) || all[0] || null;
  };

  const glide = (target, name) => {
    target.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' });
    // images that load during the glide can push the target down: one correction when the scroll ends
    const settle = () => { const off = target.getBoundingClientRect().top - (parseFloat(getComputedStyle(target).scrollMarginTop) || 0); if (Math.abs(off) > 2) target.scrollIntoView({ behavior: 'auto', block: 'start' }); };
    if ('onscrollend' in window) window.addEventListener('scrollend', settle, { once: true }); else setTimeout(settle, 900);
    if (name && location.hash !== `#${name}`) history.pushState(null, '', `#${name}`);
  };

  const targetOf = (href) => {
    try {
      const url = new URL(href, location.href);
      if (!url.hash || url.origin !== location.origin || url.pathname !== location.pathname) return null;
      return decodeURIComponent(url.hash.slice(1));
    } catch (error) {
      return null;
    }
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const trigger = event.target.closest?.('[data-scroll-to], a[href*="#"]');
    if (!trigger || trigger.closest('[data-anchor-chip]')) return;
    const name = trigger.dataset.scrollTo || targetOf(trigger.getAttribute('href'));
    const target = find(name);
    if (!target) return;
    event.preventDefault();
    glide(target, name);
  });

  window.addEventListener('load', () => {
    const name = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    const target = find(name);
    if (target) requestAnimationFrame(() => glide(target));
  });

  // ---- theme editor: ID chips
  if (!window.Shopify?.designMode) return;
  // Theme settings > Theme editor > "Show setup warnings in the editor" off: no red chips for IDs used twice.
  const warn = document.body.dataset.editorWarnings !== 'off';
  let layer;
  const chips = new Map();

  const draw = () => {
    layer ||= document.body.appendChild(Object.assign(document.createElement('div'), { className: 'anchor-chips' }));
    const all = [...document.querySelectorAll('[data-anchor]')];
    const counts = {};
    all.forEach((el) => { if (visible(el)) counts[el.dataset.anchor] = (counts[el.dataset.anchor] || 0) + 1; });
    const wanted = all.filter((el) => visible(el) && el.hasAttribute('data-anchor-show'));
    chips.forEach((chip, el) => { if (!wanted.includes(el)) { chip.remove(); chips.delete(el); } });
    wanted.forEach((el) => {
      let chip = chips.get(el);
      if (!chip) {
        chip = Object.assign(document.createElement('button'), { type: 'button', className: 'anchor-chip' });
        chip.dataset.anchorChip = '';
        chip.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          try { await navigator.clipboard.writeText(`#${el.dataset.anchor}`); } catch (error) { return; }
          chip.dataset.copied = '';
          setTimeout(() => { delete chip.dataset.copied; }, 1200);
        });
        layer.append(chip);
        chips.set(el, chip);
      }
      const twice = warn && counts[el.dataset.anchor] > 1;
      chip.textContent = `#${el.dataset.anchor}`;
      chip.title = twice ? 'This ID is used twice on the page: rename one of them. Click to copy.' : 'Click to copy';
      chip.toggleAttribute('data-duplicate', twice);
      const box = el.getBoundingClientRect();
      chip.style.transform = `translate(${Math.max(4, box.left + 4) + scrollX}px, ${Math.max(4, box.top + 4) + scrollY}px)`;
    });
  };

  let frame;
  const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(draw); };
  ['load', 'resize'].forEach((type) => window.addEventListener(type, schedule));
  ['shopify:section:load', 'shopify:section:unload', 'shopify:block:select', 'shopify:block:deselect', 'shopify:section:reorder'].forEach((type) => document.addEventListener(type, () => setTimeout(schedule, 50)));
  new ResizeObserver(schedule).observe(document.body);
  schedule();
})();
